import type { CreateUploadDto, UpdateUploadDto, UploadDto } from '@/castapi';
import { AppLogger } from '@/logger';
import { getErrorMessage, getUploadsApi } from '@/castapi/helpers';
import type { IActionParams } from '@/store/modules/index';

const logger = new AppLogger('adminUploads state');

interface IAdminUploadsState {
  uploads: UploadDto[];
  uploadsLoading: boolean;
  uploadsLoadError?: string;
  uploadSaving: boolean;
  uploadSaveError?: string;
}

const initialState = (): IAdminUploadsState => ({
  uploads: [],
  uploadsLoading: false,
  uploadsLoadError: undefined,
  uploadSaving: false,
  uploadSaveError: undefined,
});

type ActionParams = IActionParams<IAdminUploadsState>;

export default {
  namespaced: true,
  state: initialState,
  mutations: {
    UPLOADS_LOADING(state: IAdminUploadsState): void {
      state.uploadsLoadError = undefined;
      state.uploadsLoading = true;
    },
    UPLOADS_LOADED(state: IAdminUploadsState, payload: UploadDto[]): void {
      state.uploads = payload;
      state.uploadsLoading = false;
    },
    UPLOADS_LOAD_ERROR(state: IAdminUploadsState, error: Error): void {
      state.uploadsLoadError = getErrorMessage(error);
      state.uploadsLoading = false;
    },
    UPLOAD_ADD_IN_PROGRESS(state: IAdminUploadsState): void {
      state.uploadSaving = true;
      state.uploadSaveError = undefined;
    },
    UPLOAD_ADD_SUCCESS(state: IAdminUploadsState, payload: UploadDto): void {
      state.uploads.push(payload);
      state.uploadSaving = false;
    },
    UPLOAD_ADD_ERROR(state: IAdminUploadsState, error: Error): void {
      state.uploadSaving = false;
      state.uploadSaveError = getErrorMessage(error);
    },
    UPLOAD_UPDATE_IN_PROGRESS(state: IAdminUploadsState): void {
      state.uploadSaving = true;
      state.uploadSaveError = undefined;
    },
    UPLOAD_UPDATE_SUCCESS(state: IAdminUploadsState, payload: UploadDto): void {
      const ind = state.uploads.findIndex(item => item.id === payload.id);
      Object.assign(state.uploads[ind], payload);
      state.uploadSaving = false;
    },
    UPLOAD_UPDATE_ERROR(state: IAdminUploadsState, error: Error): void {
      state.uploadSaving = false;
      state.uploadSaveError = getErrorMessage(error);
    },
    UPLOAD_DELETE_IN_PROGRESS(state: IAdminUploadsState): void {
      state.uploadSaving = true;
      state.uploadSaveError = undefined;
    },
    UPLOAD_DELETE_SUCCESS(state: IAdminUploadsState, id: number): void {
      state.uploads = state.uploads.filter(item => item.id !== id);
      state.uploadSaving = false;
    },
    UPLOAD_DELETE_ERROR(state: IAdminUploadsState, error: Error): void {
      state.uploadSaving = false;
      state.uploadSaveError = getErrorMessage(error);
    },
    RESET_STATE(state: IAdminUploadsState): void {
      Object.assign(state, initialState());
    },
    RESET_EDITOR_STATE(state: IAdminUploadsState): void {
      state.uploadSaveError = undefined;
      state.uploadSaving = false;
    },
  },
  actions: {
    async loadUploads({ commit, rootGetters }: ActionParams): Promise<void> {
      const accessToken = rootGetters['login/accessToken'];
      const isEduMode = rootGetters['adminEdu/isEduMode'];
      try {
        commit('UPLOADS_LOADING');
        const response = await getUploadsApi(accessToken).uploadsControllerFindAll(isEduMode);
        commit('UPLOADS_LOADED', response.data);
      } catch (error) {
        commit('UPLOADS_LOAD_ERROR', error);
        logger.captureStoreError('loadUploads', error);
      }
    },
    async addUpload({ commit, rootGetters }: ActionParams, data: CreateUploadDto): Promise<void> {
      const accessToken = rootGetters['login/accessToken'];
      commit('UPLOAD_ADD_IN_PROGRESS');
      try {
        const response = await getUploadsApi(accessToken).uploadsControllerCreate(data);
        commit('UPLOAD_ADD_SUCCESS', response.data);
      } catch (error) {
        commit('UPLOAD_ADD_ERROR', error);
        logger.captureStoreError('addUpload', error, { data });
      }
    },
    async updateUpload({ commit, rootGetters }: ActionParams, data: UpdateUploadDto): Promise<void> {
      const accessToken = rootGetters['login/accessToken'];
      commit('UPLOAD_UPDATE_IN_PROGRESS');
      try {
        await getUploadsApi(accessToken).uploadsControllerUpdate(data.id, data);
        commit('UPLOAD_UPDATE_SUCCESS', data);
      } catch (error) {
        commit('UPLOAD_UPDATE_ERROR', error);
        logger.captureStoreError('updateUpload', error, { data });
      }
    },
    async removeUpload({ commit, rootGetters }: ActionParams, id: number): Promise<void> {
      const accessToken = rootGetters['login/accessToken'];
      commit('UPLOAD_DELETE_IN_PROGRESS');
      try {
        await getUploadsApi(accessToken).uploadsControllerRemove(id);
        commit('UPLOAD_DELETE_SUCCESS', id);
      } catch (error) {
        commit('UPLOAD_DELETE_ERROR', error);
        logger.captureStoreError('removeUpload', error, { id });
      }
    },
    resetState({ commit }: ActionParams): void {
      commit('RESET_STATE');
    },
    resetEditorState({ commit }: ActionParams): void {
      commit('RESET_EDITOR_STATE');
    },
  },
  getters: {
    uploads: (state: IAdminUploadsState): UploadDto[] => state.uploads,
    uploadsLoading: (state: IAdminUploadsState): boolean => state.uploadsLoading,
    uploadsLoadError: (state: IAdminUploadsState): undefined | string => state.uploadsLoadError,
    uploadSaving: (state: IAdminUploadsState): boolean => state.uploadSaving,
    uploadSaveError: (state: IAdminUploadsState): undefined | string => state.uploadSaveError,
  },
};
